import { Link } from 'react-router-dom'

const effectiveDate = '30 September 2026'

export function Privacy() {
  return <>
    <section className="legal-hero public-container">
      <span className="public-kicker">Legal</span>
      <h1>Privacy Policy</h1>
      <p>
        This policy explains how Mail Percent handles account information, connected mailbox data and operational
        email-work measurements.
      </p>
      <span className="legal-effective">Effective {effectiveDate}</span>
    </section>

    <section className="legal-section public-container">
      <div className="legal-layout">
        <aside className="legal-summary">
          <strong>Privacy at a glance</strong>
          <p>
            Mail Percent is designed to measure customer-email operations while limiting the customer-email content
            stored by the platform.
          </p>
          <div>
            <span>Google connection</span>
            <b>User-authorised Gmail metadata</b>
          </div>
          <div>
            <span>Core measurement</span>
            <b>Operational metadata & timestamps</b>
          </div>
          <div>
            <span>Support contact</span>
            <a href="mailto:jonathanayabulela35@gmail.com">jonathanayabulela35@gmail.com</a>
          </div>
        </aside>

        <article className="legal-copy">
          <section>
            <h2>1. Who this policy applies to</h2>
            <p>
              This Privacy Policy applies to Mail Percent, including the public website, organisation workspaces,
              Google and Microsoft mailbox integrations used by managers and invited employees.
            </p>
            <p>
              Mail Percent is intended for organisations and teams that use email to communicate with customers,
              prospects and other business contacts.
            </p>
          </section>

          <section>
            <h2>2. Information you provide to Mail Percent</h2>
            <p>We may process information that you or your organisation provides when using the service, including:</p>
            <ul>
              <li>your name, work email address and authentication information;</li>
              <li>organisation, workspace, team and role information;</li>
              <li>SLA target, category, operating-hour and other workspace settings;</li>
              <li>support requests and other information you choose to send to us.</li>
            </ul>
          </section>

          <section>
            <h2>3. Connected mailbox and Google user data</h2>
            <p>
              When a user connects Gmail or Outlook, Google or Microsoft presents the permissions requested by Mail Percent. The user authorises that mailbox connection through OAuth. Managers may also connect shared team mailboxes where they are authorised to do so.
            </p>
            <p>
              Mail Percent's core mailbox-metadata path is designed to work with operational information such as
              provider message and thread identifiers, timestamps, direction, labels and synchronisation state. The
              core metadata path may store the email subject line so authorised managers can identify measured work, but it is not designed to store email bodies, message snippets, attachments, or
              customer sender and recipient text. For shared-mailbox accountability, Mail Percent may store the work
              email identity that Google or Microsoft exposes for an assigned employee who sent a reply, solely to
              attribute that handled email to the correct team member.
            </p>
            <p>
              Mail Percent may retrieve provider metadata and timestamps for connected mailboxes through the Gmail API or Microsoft Graph. These records are used to calculate response timing, workload, queue pressure, SLA Performance and related operational reporting. For Microsoft 365 organisations that explicitly enable Advanced Send As attribution, a tenant administrator may also grant Mail Percent Exchange audit-query access. Mail Percent uses those audit records only to identify the authorised employee who performed a Send As or Send on Behalf action for an assigned shared mailbox. Mail Percent stores only the minimum attribution fields needed for that purpose and does not intentionally retain audit-log message subjects or message bodies. This API-based measurement path does not require browser extensions or keystroke capture.
            </p>
          </section>

          <section>
            <h2>4. How we use information</h2>
            <p>Mail Percent uses information to:</p>
            <ul>
              <li>create and secure accounts and organisation workspaces;</li>
              <li>connect and maintain user-authorised Gmail and Microsoft mailbox integrations;</li>
              <li>measure customer-email response timing, queue pressure, workload and SLA Performance;</li>
              <li>apply organisation and team SLA targets and produce SLA Performance and operational reports;</li>
              <li>maintain, troubleshoot and improve the service;</li>
              <li>respond to support, security and legal requests.</li>
            </ul>
            <p>
              We do not use Google user data for advertising, and we do not sell Google user data.
            </p>
          </section>

          <section>
            <h2>5. Google API Services Limited Use</h2>
            <p>
              Mail Percent's use and transfer of information received from Google APIs is intended to comply with the
              Google API Services User Data Policy, including Google's Limited Use requirements. Google user data is
              used only to provide or improve user-facing Mail Percent functionality that is clear from the product
              and the permissions requested.
            </p>
          </section>

          <section>
            <h2>6. How information is shared</h2>
            <p>
              We may share information with service providers that help us host, secure and operate Mail Percent, but
              only to the extent reasonably necessary for them to provide those services. This may include
              infrastructure, database, authentication, hosting and connected-email providers.
            </p>
            <p>
              We may also disclose information where required by law, to protect the security or rights of Mail
              Percent and its users, or as part of a legitimate business reorganisation subject to appropriate
              safeguards.
            </p>
          </section>

          <section>
            <h2>7. Storage, retention and deletion</h2>
            <p>
              Mail Percent retains account, connection and operational information for as long as reasonably needed
              to provide the service, maintain workspace reporting, meet security requirements and comply with
              applicable law.
            </p>
            <p>
              Disconnecting a mailbox stops future access through that connection. Organisation owners or users may
              also contact us to request deletion of personal information or account data, subject to legitimate
              retention requirements and the rights of the organisation that controls the workspace.
            </p>
          </section>

          <section>
            <h2>8. Security</h2>
            <p>
              We use technical and organisational safeguards intended to protect information against unauthorised
              access, alteration, disclosure or loss. Provider access tokens and refresh authorization are handled server-side and protected using technical safeguards. Sensitive connection credentials are not intentionally exposed in the public application interface.
            </p>
            <p>
              No online service can guarantee absolute security. Users and organisations are responsible for
              protecting their own accounts, devices and credentials.
            </p>
          </section>

          <section>
            <h2>9. International processing</h2>
            <p>
              Mail Percent and its service providers may process information in countries other than the country in
              which a user is located. Where applicable, we use reasonable safeguards for international processing
              and transfers.
            </p>
          </section>

          <section>
            <h2>10. Your privacy rights</h2>
            <p>
              Depending on applicable law, you may have rights to request access to, correction of, deletion of, or
              restrictions on certain personal information, and to object to certain processing. South African users
              may also have rights under the Protection of Personal Information Act (POPIA).
            </p>
            <p>
              Requests can be sent to <a href="mailto:jonathanayabulela35@gmail.com">jonathanayabulela35@gmail.com</a>.
              We may need to verify your identity and, for workspace-controlled data, confirm the organisation's
              authority before completing a request.
            </p>
          </section>

          <section>
            <h2>11. Children</h2>
            <p>
              Mail Percent is a business service and is not intended for children. We do not knowingly design the
              service for use by people under 18.
            </p>
          </section>

          <section>
            <h2>12. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy as Mail Percent changes. The current version will remain available on
              this page and the effective date above will be updated when material changes are published.
            </p>
          </section>

          <section>
            <h2>13. Contact</h2>
            <p>
              Questions about this policy or Mail Percent's handling of personal information can be sent to
              {' '}<a href="mailto:jonathanayabulela35@gmail.com">jonathanayabulela35@gmail.com</a>.
            </p>
            <p>
              You can also review our <Link to="/terms">Terms of Service</Link>.
            </p>
          </section>
        </article>
      </div>
    </section>
  </>
}
